import {
  copyPuckBlockStrings,
  transferPuckBlockStrings,
} from "@/api/cms-puck/pages";
import {
  copyBlockToClipboard,
  hasBlockClipboard,
  isTextEditingTarget,
  readBlockClipboard,
} from "@/lib/cms-puck/block-clipboard";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import {
  CONTEXTUAL_BLOCK_TYPES,
  PUCK_ROOT_ZONE,
  canPasteIntoZone,
  collectPuckSubtreeIds,
  findPuckNodeById,
  findPuckNodePath,
  insertPuckNodeAtZone,
  regeneratePuckSubtreeIds,
  type PuckTreeNode,
} from "@/lib/cms-puck/puck-tree";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  ActionBar,
  usePuck,
  type Data,
} from "@measured/puck";
import { ClipboardPaste, Copy, Scissors } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

type ClipboardContextValue = {
  pageId: string;
  /** Remap le cache local `baselineStrings` après paste / duplicate-like. */
  onStringsRemapped: (mappings: Record<string, string>) => void;
};

const PuckBlockClipboardContext = createContext<ClipboardContextValue | null>(
  null,
);

export function PuckBlockClipboardProvider({
  pageId,
  onStringsRemapped,
  children,
}: ClipboardContextValue & { children: ReactNode }) {
  return (
    <PuckBlockClipboardContext.Provider value={{ pageId, onStringsRemapped }}>
      {children}
    </PuckBlockClipboardContext.Provider>
  );
}

function useClipboardCtx(): ClipboardContextValue {
  const ctx = useContext(PuckBlockClipboardContext);
  if (!ctx) {
    throw new Error("PuckBlockClipboardProvider manquant");
  }
  return ctx;
}

/** `null` = fin de zone racine ; `undefined` = après la sélection courante. */
export type PasteAfterBlockId = string | null | undefined;

function cloneSubtreeById(data: Data, id: string | null): PuckTreeNode | null {
  if (!id) return null;
  const live = findPuckNodeById(data, id);
  if (!live) return null;
  return structuredClone(live) as PuckTreeNode;
}

function cloneSelectedSubtree(
  data: Data,
  selected: { type?: string; props?: Record<string, unknown> } | null | undefined,
): PuckTreeNode | null {
  if (!selected?.type) return null;
  const id =
    typeof selected.props?.id === "string" ? selected.props.id : null;
  return cloneSubtreeById(data, id);
}

function resolvePasteAnchor(
  data: Data,
  afterBlockId: PasteAfterBlockId,
  selectedItem: { props?: Record<string, unknown> } | null | undefined,
): { zone: string; insertIndex: number } {
  // Clic droit page vide → toujours en fin de page, ignore la sélection.
  if (afterBlockId === null) {
    return {
      zone: PUCK_ROOT_ZONE,
      insertIndex: getZoneLength(data, PUCK_ROOT_ZONE),
    };
  }

  const anchorId =
    afterBlockId !== undefined
      ? afterBlockId
      : typeof selectedItem?.props?.id === "string"
        ? selectedItem.props.id
        : null;
  const path = anchorId ? findPuckNodePath(data, anchorId) : null;
  const last = path?.[path.length - 1];
  const zone = last?.zone ?? PUCK_ROOT_ZONE;
  return {
    zone,
    insertIndex:
      last != null ? last.index + 1 : getZoneLength(data, zone),
  };
}

function useBlockClipboardActions() {
  const cmsNew = useCmsNew();
  const { toast } = useToast();
  const { pageId, onStringsRemapped } = useClipboardCtx();
  const { selectedItem, dispatch, appState, config, getSelectorForId } =
    usePuck();
  const busyRef = useRef(false);

  const persistStrings = useCallback(
    (sourcePageId: string, mappings: Record<string, string>) => {
      onStringsRemapped(mappings);
      const req =
        sourcePageId === pageId
          ? copyPuckBlockStrings(pageId, mappings)
          : transferPuckBlockStrings(pageId, { sourcePageId, mappings });
      void req.catch(() => {
        toast({
          title: cmsNew(
            "cms.new.auto.title.traductions_non_copiees",
            "Traductions non copiées",
          ),
          description: cmsNew(
            "cms.new.editor.clipboard.stringsFailed",
            "Le bloc a été collé mais ses traductions n'ont pas pu être copiées.",
          ),
          variant: "error",
          duration: 3500,
        });
      });
    },
    [cmsNew, onStringsRemapped, pageId, toast],
  );

  const copy = useCallback(
    async (blockId?: string) => {
      const subtree = blockId
        ? cloneSubtreeById(appState.data, blockId)
        : cloneSelectedSubtree(appState.data, selectedItem);
      if (!subtree) {
        toast({
          title: cmsNew(
            "cms.new.editor.clipboard.noSelection",
            "Aucun bloc sélectionné",
          ),
          variant: "error",
          duration: 2000,
        });
        return false;
      }
      await copyBlockToClipboard({ sourcePageId: pageId, subtree });
      toast({
        title: cmsNew("cms.new.editor.clipboard.copied", "Bloc copié"),
        variant: "success",
        duration: 1400,
      });
      return true;
    },
    [appState.data, cmsNew, pageId, selectedItem, toast],
  );

  const paste = useCallback(
    async (afterBlockId?: PasteAfterBlockId) => {
      if (busyRef.current) return;
      busyRef.current = true;
      try {
        const payload = await readBlockClipboard();
        if (!payload) {
          toast({
            title: cmsNew(
              "cms.new.editor.clipboard.empty",
              "Presse-papiers vide",
            ),
            description: cmsNew(
              "cms.new.editor.clipboard.emptyHint",
              "Copie d'abord un bloc (Cmd/Ctrl+C).",
            ),
            variant: "error",
            duration: 2500,
          });
          return;
        }

        const nodeType = payload.subtree.type ?? "";
        const { zone, insertIndex } = resolvePasteAnchor(
          appState.data,
          afterBlockId,
          selectedItem,
        );

        if (!canPasteIntoZone(appState.data, zone, nodeType, config)) {
          toast({
            title: cmsNew(
              "cms.new.editor.clipboard.slotDenied",
              "Collage impossible ici",
            ),
            description: cmsNew(
              "cms.new.editor.clipboard.slotDeniedHint",
              "Ce type de bloc n'est pas autorisé dans ce conteneur.",
            ),
            variant: "error",
            duration: 3000,
          });
          return;
        }

        const { node, mappings } = regeneratePuckSubtreeIds(
          structuredClone(payload.subtree),
        );
        if (Object.keys(mappings).length === 0) return;

        const nextData = insertPuckNodeAtZone(
          appState.data,
          zone,
          insertIndex,
          node,
        );
        dispatch({ type: "setData", data: nextData });

        const sourceRootId = collectPuckSubtreeIds(payload.subtree)[0];
        const pastedId = sourceRootId ? mappings[sourceRootId] : undefined;
        dispatch({
          type: "setUi",
          ui: {
            itemSelector: pastedId
              ? (getSelectorForId(pastedId) ?? {
                  zone,
                  index: insertIndex,
                })
              : { zone, index: insertIndex },
          },
          recordHistory: false,
        });

        persistStrings(payload.sourcePageId, mappings);

        if (CONTEXTUAL_BLOCK_TYPES.has(nodeType)) {
          toast({
            title: cmsNew(
              "cms.new.editor.clipboard.contextualWarn",
              "Bloc contextuel collé",
            ),
            description: cmsNew(
              "cms.new.editor.clipboard.contextualWarnHint",
              "Ce bloc est plutôt destiné à l'en-tête, au pied de page ou au catalogue.",
            ),
            duration: 3500,
          });
        } else {
          toast({
            title: cmsNew("cms.new.editor.clipboard.pasted", "Bloc collé"),
            variant: "success",
            duration: 1400,
          });
        }
      } finally {
        busyRef.current = false;
      }
    },
    [
      appState.data,
      cmsNew,
      config,
      dispatch,
      getSelectorForId,
      persistStrings,
      selectedItem,
      toast,
    ],
  );

  const cut = useCallback(
    async (blockId?: string) => {
      const selectedId =
        blockId ??
        (typeof selectedItem?.props?.id === "string"
          ? selectedItem.props.id
          : null);
      if (!selectedId) {
        toast({
          title: cmsNew(
            "cms.new.editor.clipboard.noSelection",
            "Aucun bloc sélectionné",
          ),
          variant: "error",
          duration: 2000,
        });
        return;
      }
      const path = findPuckNodePath(appState.data, selectedId);
      const last = path?.[path.length - 1];
      if (!last) return;

      const subtree = cloneSubtreeById(appState.data, selectedId);
      if (!subtree) return;
      await copyBlockToClipboard({ sourcePageId: pageId, subtree });

      dispatch({
        type: "remove",
        index: last.index,
        zone: last.zone,
      });
      toast({
        title: cmsNew("cms.new.editor.clipboard.cut", "Bloc coupé"),
        variant: "success",
        duration: 1400,
      });
    },
    [appState.data, cmsNew, dispatch, pageId, selectedItem, toast],
  );

  return { copy, paste, cut, selectedItem };
}

function getZoneLength(data: Data, zone: string): number {
  if (zone === PUCK_ROOT_ZONE) {
    return Array.isArray(data.content) ? data.content.length : 0;
  }
  const colon = zone.lastIndexOf(":");
  if (colon <= 0) return 0;
  const parentId = zone.slice(0, colon);
  const slotKey = zone.slice(colon + 1);
  const parent = findPuckNodeById(data, parentId);
  const slot = parent?.props?.[slotKey];
  return Array.isArray(slot) ? slot.length : 0;
}

/** Boutons Copier / Couper / Coller dans la barre d'actions du bloc. */
export function PuckBlockClipboardActionBar({
  label,
  children,
  parentAction,
}: {
  label?: string;
  children: ReactNode;
  parentAction: ReactNode;
}) {
  const cmsNew = useCmsNew();
  const { copy, paste, cut } = useBlockClipboardActions();

  return (
    <ActionBar label={label}>
      {parentAction}
      <ActionBar.Group>
        <ActionBar.Action
          label={cmsNew("cms.new.editor.clipboard.copy", "Copier")}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void copy();
          }}
        >
          <Copy size={14} />
        </ActionBar.Action>
        <ActionBar.Action
          label={cmsNew("cms.new.editor.clipboard.cut", "Couper")}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void cut();
          }}
        >
          <Scissors size={14} />
        </ActionBar.Action>
        <ActionBar.Action
          label={cmsNew("cms.new.editor.clipboard.paste", "Coller")}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void paste();
          }}
        >
          <ClipboardPaste size={14} />
        </ActionBar.Action>
      </ActionBar.Group>
      {children}
    </ActionBar>
  );
}

/**
 * Raccourcis Cmd/Ctrl+C/V/X focus-aware.
 * - Sans `listenDocument` : écoute la fenêtre parente (sidebar, chrome Puck).
 * - Avec `listenDocument` : écoute le document de l'iframe canevas — indispensable
 *   car le focus y est souvent après un clic sur la page, et les events iframe
 *   n'atteignent pas `window` parent.
 * Coller sans sélection = insertion en fin de zone racine.
 */
export function PuckBlockClipboardHotkeys({
  listenDocument,
}: {
  listenDocument?: Document | null;
} = {}) {
  const { copy, paste, cut, selectedItem } = useBlockClipboardActions();

  useEffect(() => {
    // Cast EventListener : Document|Window unifié + KeyboardEvent cross-realm iframe.
    const onKeyDown = (raw: Event) => {
      const e = raw as KeyboardEvent;
      if (typeof e.key !== "string") return;
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (isTextEditingTarget(e.target)) return;

      const key = e.key.toLowerCase();
      if (key === "c") {
        if (!selectedItem?.props?.id) return;
        e.preventDefault();
        void copy();
        return;
      }
      if (key === "x") {
        if (!selectedItem?.props?.id) return;
        e.preventDefault();
        void cut();
        return;
      }
      if (key === "v") {
        e.preventDefault();
        void paste();
      }
    };

    const target: Document | Window = listenDocument ?? window;
    // iframe pas encore montée / document null → rien à attacher
    if (listenDocument === null) return;

    target.addEventListener("keydown", onKeyDown, true);
    return () => target.removeEventListener("keydown", onKeyDown, true);
  }, [copy, cut, listenDocument, paste, selectedItem]);

  return null;
}

type ContextMenuState = {
  x: number;
  y: number;
  /** Bloc sous le curseur ; `null` = page vide → collage en fin de page. */
  blockId: string | null;
};

/**
 * Bouton « Coller » au clic droit sur le canevas.
 * Position parent = coords iframe × scale Puck (`transform: scale(zoom)`).
 */
export function PuckBlockClipboardContextMenu({
  listenDocument,
}: {
  listenDocument?: Document | null;
}) {
  const cmsNew = useCmsNew();
  const { paste } = useBlockClipboardActions();
  const [menu, setMenu] = useState<ContextMenuState | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const canPaste = hasBlockClipboard();

  const close = useCallback(() => setMenu(null), []);

  useEffect(() => {
    if (!listenDocument) return;

    const onContextMenu = (e: MouseEvent) => {
      if (isTextEditingTarget(e.target)) return;
      const raw = e.target;
      if (raw == null || typeof raw !== "object") return;
      const el = raw as { closest?: (s: string) => Element | null };
      if (typeof el.closest !== "function") return;

      const blockEl = el.closest("[data-puck-component]");
      const blockId = blockEl?.getAttribute("data-puck-component") ?? null;

      e.preventDefault();
      e.stopPropagation();

      const frameWin = listenDocument.defaultView;
      const iframeEl = frameWin?.frameElement;
      const iframe =
        iframeEl && typeof (iframeEl as HTMLElement).getBoundingClientRect === "function"
          ? (iframeEl as HTMLElement)
          : null;
      const rect = iframe?.getBoundingClientRect();
      // Puck applique `scale(zoom)` sur l'iframe : clientX/Y sont en coords
      // logiques, getBoundingClientRect est déjà zoomé.
      const layoutW = iframe
        ? (iframe as HTMLIFrameElement).offsetWidth || rect?.width || 1
        : 1;
      const layoutH = iframe
        ? (iframe as HTMLIFrameElement).offsetHeight || rect?.height || 1
        : 1;
      const scaleX = rect && layoutW > 0 ? rect.width / layoutW : 1;
      const scaleY = rect && layoutH > 0 ? rect.height / layoutH : 1;
      // Juste à droite du curseur
      const rawX = (rect?.left ?? 0) + e.clientX * scaleX + 6;
      const rawY = (rect?.top ?? 0) + e.clientY * scaleY;
      const x = Math.min(Math.max(8, rawX), window.innerWidth - 44);
      const y = Math.min(Math.max(8, rawY), window.innerHeight - 44);

      setMenu({ x, y, blockId });
    };

    listenDocument.addEventListener("contextmenu", onContextMenu, true);
    return () =>
      listenDocument.removeEventListener("contextmenu", onContextMenu, true);
  }, [listenDocument]);

  useEffect(() => {
    if (!menu) return;

    const onPointerDown = (e: MouseEvent) => {
      const node = menuRef.current;
      if (node && e.target instanceof Node && node.contains(e.target)) return;
      close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const onScroll = () => close();

    window.addEventListener("mousedown", onPointerDown, true);
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("scroll", onScroll, true);
    listenDocument?.addEventListener("mousedown", onPointerDown, true);
    listenDocument?.addEventListener("scroll", onScroll, true);

    return () => {
      window.removeEventListener("mousedown", onPointerDown, true);
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("scroll", onScroll, true);
      listenDocument?.removeEventListener("mousedown", onPointerDown, true);
      listenDocument?.removeEventListener("scroll", onScroll, true);
    };
  }, [close, listenDocument, menu]);

  if (!menu) return null;

  const pasteLabel = cmsNew("cms.new.editor.clipboard.paste", "Coller");

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      style={{ position: "fixed", left: menu.x, top: menu.y, zIndex: 10000 }}
    >
      <ActionBar>
        <ActionBar.Group>
          <ActionBar.Action
            label={pasteLabel}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!canPaste) return;
              const after = menu.blockId;
              close();
              void paste(after);
            }}
          >
            <span
              className={cn(!canPaste && "pointer-events-none opacity-40")}
              aria-disabled={!canPaste}
            >
              <ClipboardPaste size={14} />
            </span>
          </ActionBar.Action>
        </ActionBar.Group>
      </ActionBar>
    </div>,
    document.body,
  );
}
